import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Ticket } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";

const PROVIDERS = [
  { key: "1voucher", name: "1Voucher", len: 16, where: "Shoprite, Checkers, Pick n Pay, Usave, banking apps" },
  { key: "ott", name: "OTT Voucher", len: 12, where: "Spaza shops, PEP, Ackermans, online resellers" },
  { key: "blu", name: "Blu Voucher", len: 16, where: "Spar, Boxer, Game, online resellers" },
];

const STATUS = { pending: "text-[#F59E0B]", approved: "text-[#10B981]", rejected: "text-[#EF4444]" };

export const VoucherList = () => {
  const { data } = useQuery({ queryKey: ["vouchers"], queryFn: () => api.get("/wallet/vouchers").then((r) => r.data), refetchInterval: 15000 });
  if (!data?.length) return null;
  return (
    <div className="space-y-1.5 pt-2">
      <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400">Your voucher deposits</h4>
      {data.map((v) => (
        <div key={v.id} data-testid={`voucher-row-${v.id}`} className="flex items-center justify-between rounded-md bg-[#0E131F] px-3 py-2 text-xs">
          <div>
            <div className="font-semibold">{v.provider_name} {v.pin_masked}</div>
            <div className="text-slate-500">{(v.verified_amount || v.amount).toFixed(2)} {v.currency} · {new Date(v.created_at).toLocaleString()} {v.admin_note && `· ${v.admin_note}`}</div>
          </div>
          <span className={`font-bold uppercase ${STATUS[v.status]}`}>{v.status === "pending" ? "verifying" : v.status}</span>
        </div>
      ))}
    </div>
  );
};

export const VoucherForm = ({ onDone }) => {
  const { currency, fromZar, limits } = useCurrency();
  const [prov, setProv] = useState(PROVIDERS[0]);
  const [pin, setPin] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const min = Math.ceil(fromZar(limits.min_deposit_zar));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/wallet/voucher", { provider: prov.key, pin, amount: parseFloat(amount), currency });
      toast.success("Voucher received — your wallet is credited as soon as it's verified.");
      setPin("");
      setAmount("");
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="grid grid-cols-3 gap-2">
        {PROVIDERS.map((p) => (
          <button type="button" key={p.key} data-testid={`voucher-provider-${p.key}`} onClick={() => { setProv(p); setPin(""); }}
            className={`rounded-lg border px-2 py-3 text-center text-xs font-bold transition-colors ${prov.key === p.key ? "border-[#10B981] bg-[#10B981]/10 text-[#10B981]" : "border-[#1E293B] bg-[#0E131F] text-slate-300 hover:border-[#10B981]/50"}`}>
            <Ticket className="mx-auto mb-1 h-4 w-4" /> {p.name}
          </button>
        ))}
      </div>
      <p className="text-[11px] text-slate-500">Buy a {prov.name} at {prov.where}.</p>
      <div className="relative">
        <Input data-testid="voucher-pin-input" inputMode="numeric" maxLength={prov.len} placeholder={`${prov.len}-digit voucher PIN`} value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, prov.len))} className="pr-14 font-odds tracking-widest" required />
        <span data-testid="voucher-pin-counter" className={`absolute right-3 top-2 font-odds text-xs ${pin.length === prov.len ? "text-[#10B981]" : "text-slate-500"}`}>
          {pin.length}/{prov.len}
        </span>
      </div>
      <Input data-testid="voucher-amount-input" type="number" min={min} step="0.01" placeholder={`Voucher value (min ${min} ${currency})`} value={amount}
        onChange={(e) => setAmount(e.target.value)} required />
      <Button data-testid="voucher-submit-button" disabled={busy} className="w-full bg-[#10B981] font-bold text-[#0B0E14] hover:bg-[#059669]">
        {busy ? "Submitting…" : `Redeem ${prov.name}`}
      </Button>
      <p className="text-[11px] text-slate-500">Vouchers are verified with the reseller and credited to your wallet, usually within minutes.</p>
    </form>
  );
};
